import React from "react";
import LabelValue from "../../shared/custom/labelValue/LabelValue";
import { useDateCustom } from "../../shared/custom_use/useDateCustom";
import { isArray } from "../../shared/utils/arrayUtils";
import { useLanguageForShared } from "../../shared/utils/languages/useLanguageForShared";
import TemplateConcentratorPrescriptionRead from "./TemplateConcentratorPrescriptionRead";
import { TemplateConcentratorPulsedFlowRead } from "./TemplateConcentratorPulsedFlowRead";
import { concentratorType } from "../concentratorData";
import { templateNameId, templateNameType } from "../templateData";

const blockKeys = [
  "flowRest",
  "flowRestHourDie",
  "underStressFlow",
  "underStressFlowHourDie",
  "nightFlow",
  "nightFlowHourDie",
  "flowType"
];

const hasPrefixedBlock = (data: any, prefix: string) => {
  if (data?.[prefix] && typeof data[prefix] === "object") {
    return true;
  }

  return blockKeys.some((key) => {
    const flatKey = `${prefix}.${key}`;
    return data?.[flatKey] !== undefined && data?.[flatKey] !== null && data?.[flatKey] !== "";
  });
};

export function TemplateConcentratorCustomRead({ data, template }: any) {
  const { t } = useLanguageForShared();
  const { data: dataFormatter } = useDateCustom();
  const isFixedTemplate = template === templateNameId[templateNameType.concentratorFixed];
  const isPortableTemplate = template === templateNameId[templateNameType.concentratorPortable];
  const overallConcentratorLabel =
    isFixedTemplate && data?.concentratorType === concentratorType.transportable
      ? t("concentrator.fixed_transportable")
      : t(`concentrator.${data?.concentratorType}`);

  const getSharedValidUntil = () => {
    return data?.validUntil
      || data?.fixed?.validUntil
      || data?.portable?.validUntil
      || data?.transportable?.validUntil
      || data?.["fixed.validUntil"]
      || data?.["portable.validUntil"]
      || data?.["transportable.validUntil"];
  };

  const sharedValidUntil = getSharedValidUntil();

  const checkPathologyType = () => {
    return isArray(data?.pathology)
      ? data?.pathology?.map((ele: string) => t(`pathology.${ele}`)).join(", ")
      : data?.pathology || "-";
  };

  const blocks = [
    ((isFixedTemplate && [concentratorType.fixed, concentratorType.fixed_portable, concentratorType.transportable].includes(data?.concentratorType)) || hasPrefixedBlock(data, "fixed"))
      && { prefix: "fixed", title: t("concentrator.fixed") },
    ((isFixedTemplate && data?.concentratorType === concentratorType.fixed_portable)
      || (isPortableTemplate && data?.concentratorType === concentratorType.portable)
      || hasPrefixedBlock(data, "portable"))
      && { prefix: "portable", title: t("concentrator.portable") },
    ((isFixedTemplate && data?.concentratorType === concentratorType.transportable)
      || (isPortableTemplate && data?.concentratorType === concentratorType.transportable)
      || hasPrefixedBlock(data, "transportable"))
      && { prefix: "transportable", title: t("concentrator.transportable") }
  ].filter(Boolean) as Array<{ prefix: string; title: string }>;

  return (
    <div className={"template-sg ventilo-template row row-gap-4"}>
      <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-7">
        <div className={"row row-gap-4"}>
          <div className="col-sm-12 col-md-6">
            <LabelValue
              label={t("common.concentrator")}
              value={overallConcentratorLabel}
            />
          </div>

          <div className="col-sm-12 col-md-6">
            <LabelValue
              label={t("prescription.template.validUntil")}
              value={sharedValidUntil ? dataFormatter.dateFormatGGmmmYY(sharedValidUntil) : "-"}
            />
          </div>

          {data?.pathology && data?.pathology.length > 0 && (
            <div className="col-sm-12 col-md-12">
              <LabelValue label={t("common.pathologies")} value={checkPathologyType()} />
              {data?.otherPathologies && (
                <LabelValue label={t("common.otherPathologies")} value={data?.otherPathologies} />
              )}
            </div>
          )}

          {blocks.length > 0 ? (
            blocks.map((block) => (
              <TemplateConcentratorPrescriptionRead
                key={block.prefix}
                data={data}
                prefix={block.prefix}
                title={block.title}
              />
            ))
          ) : (
            <TemplateConcentratorPrescriptionRead data={data} />
          )}

          <TemplateConcentratorPulsedFlowRead data={data} />
        </div>
      </div>

      <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-5">
        <div className={"row row-gap-4"}>
          <div className="col-sm-12 col-md-12">
            <p className={"title-paragraph"}>{t("sg.otherInfo")}</p>

            <LabelValue
              label={t("prescription.template.cannulaNasal.label")}
              value={data?.cannulaNasal ? t("common.yes") : t("common.no")}
              className={"me-5"}
            />
            {data?.cannulaNasal && (
              <>
                <LabelValue
                  label={t("prescription.template.cannulaType.label")}
                  value={data?.cannulaType || "-"}
                  className={"me-5"}
                />
                <LabelValue
                  label={t("prescription.template.cannulaModel.label")}
                  value={data?.cannulaModel || "-"}
                  className={"me-5"}
                />
              </>
            )}
          </div>

          <div className="col-sm-12 col-md-12">
            <LabelValue
              label={t("prescription.template.mask.label")}
              value={data?.mask ? t("common.yes") : t("common.no")}
              className={"me-5"}
            />
            {data?.mask && (
              <>
                <LabelValue
                  label={t("prescription.template.maskType.label")}
                  value={data?.maskType || "-"}
                  className={"me-5"}
                />
                <LabelValue
                  label={t("prescription.template.maskModel.label")}
                  value={data?.maskModel || "-"}
                  className={"me-5"}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className={"row row-gap-4"}>
        <div className="col-sm-12 col-md-12">
          <LabelValue label={t("prescription.template.note.label")} value={data?.note || "-"} />
        </div>
      </div>
    </div>
  );
}